<script>
import { mapActions } from 'vuex';
import axios from 'axios';
import i18n from '@/plugins/i18n';

export default {
  props: {
    kpiType: {
      type: String,
      required: true // can be either 'metric' or 'submetric' //
    },
    kpiID: {
      type: Number,
      required: true // either metric_id or submetric_id //
    },
    quarter: {
      type: Number,
      required: true
    },
    year: {
      type: Number,
      required: true
    },
    readOnly: {
      type: Boolean,
      required: false,
      default: false
    },
    companyID: {
      type: Number,
      required: false,
      default: null
    }
  },

  data() {
    return {
      searchQuery: '',
      searchModal: false,
      searchResult: [],
      noResult: false,

      parsedQuery: ['MY DOCUMENTS', 'KPIs'], // hard coded this since this will never change //
      view: 'list',

      // fetched data //

      allDocuments: [],
      documents: [],

      // form data //

      files: null,
      documentTypeID: '',
      documentPath: '',
      documentName: '',

      fileDrop: null,
      fileBrowse: null,

      // selected documents //

      selectedDocument: {},
      selectedDocumentID: '',
      selectedDocumentName: '',
      selectedDocumentPath: '',
      selectedDocumentExtension: '',

      // document editing //

      newDocumentName: '',

      // folder data //

      folderName_Create: '',
      folderName_Edit: '', // used both for editing folder name and the deleting the folder //
      newFolderName: '', // new name of the folder after the edit //
      
      // context menu controllers //
      
      menuType: '',
      contextMenu: false,
      contextMenuX: 0,
      contextMenuY: 0,

      contextMenuItem: {},

      // view document controllers //

      documentToView: '',
      token : '',
      documentEndpoint: '',
      viewDocumentModal: false,

      // modal controllers //
      renameFileModal: false,

      // event controllers //

      isDraggedOver: false,

      isMovedOver: false,

      // move item controllers //

      movedItem: {},
      movedOver: {},
      movedTo: {},
      
      // static objects //

      documentTypes: [],

      grid_data: [
        {
          type: "xlsx",
          icon: "fa-solid fa-file-excel"
        },
        {
          type: "docx",
          icon: "fa-solid fa-file-word"
        },
        {
          type: "jpg",
          icon: "fa-solid fa-file-image"
        },
        {
          type: "png",
          icon: "fa-solid fa-file-image"
        },
        {
          type: "pdf",
          icon: "fa-solid fa-file-pdf"
        },
        {
          type: "folder",
          icon: "fa-solid fa-folder"
        }
      ]
    }
  },

  computed: {
    folderLevel() {
      return this.parsedQuery.length;
    },

    selectedFiles() {
      const isCurrentQuarterAndYear = (item) => {
        const _bool = (item.info.quarter == this.quarter && item.info.year == this.year)
        return _bool;
      }

      if ( this.kpiType === 'metric') {
        return this.documentsForSearch.filter( item => item.info.metric_kpi_ids != null ? item.info.metric_kpi_ids.includes(this.kpiID) && isCurrentQuarterAndYear(item) : false);
      } else if ( this.kpiType === 'submetric') {
        return this.documentsForSearch.filter( item => item.info.sub_metric_kpi_ids != null ? item.info.sub_metric_kpi_ids.includes(this.kpiID) && isCurrentQuarterAndYear(item) : false);
      } else {
        throw new Error("Invalid KPI Type!");
      }
    },

    unselectedFiles() {
      const isCurrentQuarterAndYear = (item) => {
        const _bool = (item.info.quarter == this.quarter && item.info.year == this.year) || (item.info.quarter == null && item.info.year == null)
        return _bool;
      }

      if ( this.kpiType === 'metric') {
        return this.documentsForSearch.filter( item => item.info.metric_kpi_ids != null ? !item.info.metric_kpi_ids.includes(this.kpiID) && isCurrentQuarterAndYear(item) : true);
      } else if ( this.kpiType === 'submetric') {
        return this.documentsForSearch.filter( item => item.info.sub_metric_kpi_ids != null ? !item.info.sub_metric_kpi_ids.includes(this.kpiID) && isCurrentQuarterAndYear(item) : true);
      } else {
        throw new Error("Invalid KPI Type!");
      }
    },


    documentsForSearch() {
      if ( this.searchResult.length > 0) {
        return this.searchResult;
      } else {
        return this.documents;
      }
    }
  },

  watch: {
    searchQuery() {
      if (this.searchQuery === "") {
        this.resetSearch();
      }
    }
  },

  mounted() {
    this.documentEndpoint = axios.defaults.baseURL + '/api/auditor/docview/';

    this._getDocumentTypes();
    this._getAllDocuments();
  },

  methods: {

    // handle query params and path //

    initializePath() {
      if (this.quarter == 5) {
        this.parsedQuery = [String(this.year), "YEARLY"];
        return `KPIs/${this.year}/YEARLY`;
      } else {
        this.parsedQuery = [String(this.year), "QUARTERLY"];
        return `KPIs/${this.year}/QUARTERLY/Q${this.quarter}`;
      }
    },

    // api calls //

    ...mapActions('auditor', [
        'viewDocument',
    ]),

    ...mapActions('documentation', [
      'getDocumentTypes',
      'getAllDocuments',
      'getDocumentsByCompanyId',
      'renameDocument',
      'searchDocument',
      'uploadMetricDocument', // metric process //
      'uploadSubmetricDocument',
      'linkMetricDocument',
      'linkSubmetricDocument',
      'removeMetricDocument',
      'removeSubmetricDocument',
    ]),

    _getDocumentTypes() {
      this.getDocumentTypes()
        .then( res => {
          this.documentTypes = res.data;
        })
        .catch(({ response }) => {
          this.$toast.open({
            message: this.$parseResMessage(response.data.messageName),
            type: 'error',
            dismissible: true,
            duration: 5000
          });
        })
    },

    _getAllDocuments() {
      this.documents = [];
      
      let params = {};

      params.is_deleted = false;
      params.documents_path = this.initializePath();
      params.company_id = this.companyID ? this.companyID : this.$user.company_id

      this.getAllDocuments(params)
        .then( res => {
          for (const key in res.data) {
            if (Object.hasOwnProperty.call(res.data, key)) {
              const element = res.data[key];
              if (typeof element === 'object'){
                element.forEach( item => {
                  if (item.assigned_target_ids.length > 0) return;

                  if (item.extension === "") {
                    this.documents.push({
                      extension: "folder",
                      fileSizeInBytes: item.fileSizeInBytes,
                      name: item.name,
                      normalizedName: this.normalizeName(item.name),
                      info: item.info
                    })
                  } else {
                    this.documents.push({
                      extension: item.extension,
                      fileSizeInBytes: item.fileSizeInBytes,
                      name: item.name,
                      normalizedName: this.normalizeName(item.name),
                      info: item.info
                    })
                  }
                })
              }
            }
          }
        })
        .catch(({ response }) => {
          this.$toast.open({
            message: this.$parseResMessage(response.data.messageName),
            type: 'error',
            dismissible: true,
            duration: 5000
          });
        })
        .finally(() => {
          this._getAllDocumentsByCompanyID();
        })
    },

    _getAllDocumentsByCompanyID() {
      let params = {};

      params.is_deleted = false;
      params.company_id = this.companyID ? this.companyID : null

      this.getDocumentsByCompanyId(params)
        .then( res => {
          for (const type in res.data) {
            if (Object.hasOwnProperty.call(res.data, type)) {
              const documents = res.data[type];
              
              documents.forEach( document => {
                const exists_already = this.documents.some(item => document.info.document_id == item.info.document_id);
                if (!exists_already && document.info.assigned_target_ids.length == 0) {
                  this.documents.push({
                    extension: document.extension,
                    fileSizeInBytes: document.fileSizeInBytes,
                    name: document.name,
                    normalizedName: this.normalizeName(document.name),
                    info: document.info
                  })
                }
              })
            }
          }
        })
        .catch(({ response }) => {
          this.$toast.open({
            message: this.$parseResMessage(response.data.messageName),
            type: 'error',
            dismissible: true,
            duration: 5000
          });
        })
    },

    async _viewDocument() {
      const response = await fetch(`${this.documentEndpoint}${this.selectedDocumentID}`, {
          method: "POST",
          body: JSON.stringify({ company_id: this.companyID }),
          headers: { 'Content-Type': 'application/json' }
      });

      const blob = await response.blob();
      this.documentToView = URL.createObjectURL(blob);

    },

    _searchDocument() {
      const params = {
        query: this.searchQuery
      };
      this.searchDocument(params)
        .then( res => {
          this.searchResult = [];
          res.data.forEach( item => {
            this.searchResult.push({
              extension: "." + this.documentTypes.find(element => element.document_type_id === item.document_type_id).document_type_name,
              fileSizeInBytes: item.fileSizeInBytes,
              name: item.document_name,
              normalizedName: this.normalizeName(item.document_name),
              info: item
            })
          })
          
          // this.searchModal = true;
        })
        .catch(({ response }) => {
          this.noResult = true;
          this.$toast.open({
            message: this.$parseResMessage(response.data.messageName),
            type: 'error',
            dismissible: true,
            duration: 5000
          });
        })
        .finally(() => {
          this._getAllDocuments();
        })
    },

    resetSearch() {
      this.searchModal = false;
      this.searchQuery = "";
      this.searchResult = [];
      this.noResult = false;
    },

    _renameDocument() {
      const params = {
        document_id: this.selectedDocumentID,
        new_document_name: `${this.newDocumentName}${this.selectedDocumentExtension}`,
      }
      this.renameDocument(params)
        .then( res => {
          this.$toast.open({
            message: this.$parseResMessage(res.data.messageName),
            type: 'success',
            dismissible: true,
            duration: 5000
          });
        })
        .catch(({ response }) => {
          this.$toast.open({
            message: this.$parseResMessage(response.data.messageName),
            type: 'error',
            dismissible: true,
            duration: 5000
          });
        })
        .finally(() => {
          this._getAllDocuments();
          this.resetAll();
        })
    },

    _uploadMetricDocument() {
        const formData = new FormData();
        formData.append('files', this.files);
        formData.append('document_name', this.files.name);
        formData.append('document_type_id', this.documentTypeID);
        formData.append('metric_kpi_id', this.kpiID);
        formData.append('quarter', this.quarter);
        formData.append('year', this.year);

        this.uploadMetricDocument(formData)
          .then( res => {
            this.$toast.open({
              message: this.$parseResMessage(res.data.messageName),
              type: 'success',
              dismissible: true,
              duration: 5000
            });
          })
          .catch(({ response }) => {
            this.$toast.open({
              message: this.$parseResMessage(response.data.messageName),
              type: 'error',
              dismissible: true,
              duration: 5000
            });
          })
          .finally(() => {
            this._getAllDocuments();
            this.resetAll();
          })
    },

    _uploadSubmetricDocument() {
        const formData = new FormData();
        formData.append('files', this.files);
        formData.append('document_name', this.files.name);
        formData.append('document_type_id', this.documentTypeID);
        formData.append('sub_metric_kpi_id', this.kpiID);
        formData.append('quarter', this.quarter);
        formData.append('year', this.year);
    
        this.uploadSubmetricDocument(formData)
            .then( res => {
            this.$toast.open({
                message: this.$parseResMessage(res.data.messageName),
                type: 'success',
                dismissible: true,
                duration: 5000
            });
            })
            .catch(({ response }) => {
            this.$toast.open({
                message: this.$parseResMessage(response.data.messageName),
                type: 'error',
                dismissible: true,
                duration: 5000
            });
            })
            .finally(() => {
                this._getAllDocuments();
                this.resetAll();
            })
    },

    _linkMetricDocument() {
        const params = {
          document_id: this.selectedDocumentID,
          year: this.year,
          quarter: this.quarter,
          metric_kpi_id: this.kpiID
        };

        this.linkMetricDocument(params)
            .then( res => {
                this.$toast.open({
                    message: this.$parseResMessage(res.data.messageName),
                    type: 'success',
                    dismissible: true,
                    duration: 5000
                });
            })
            .catch(({ response }) => {
                this.$toast.open({
                    message: this.$parseResMessage(response.data.messageName),
                    type: 'error',
                    dismissible: true,
                    duration: 5000
                });
            })
            .finally(() => {
              if (this.searchQuery.length > 0) {
                this._searchDocument();
              }
              this._getAllDocuments(); // #TODO: Get the existing links and update the view //
              this.resetAll();
            })
    },

    _linkSubmetricDocument() {
        const params = {
          document_id: this.selectedDocumentID,
          year: this.year,
          quarter: this.quarter,
          sub_metric_kpi_id: this.kpiID
        };

        this.linkSubmetricDocument(params)
            .then( res => {
                this.$toast.open({
                    message: this.$parseResMessage(res.data.messageName),
                    type: 'success',
                    dismissible: true,
                    duration: 5000
                });
            })
            .catch(({ response }) => {
                this.$toast.open({
                    message: this.$parseResMessage(response.data.messageName),
                    type: 'error',
                    dismissible: true,
                    duration: 5000
                });
            })
            .finally(() => {
              if (this.searchQuery.length > 0) {
                this._searchDocument();
              }  
              this._getAllDocuments(); // #TODO: Get the existing links and update the view //
              this.resetAll();
            })
    },

    _removeMetricDocument() {
        const params = {
            document_id: this.selectedDocumentID,
            metric_kpi_id: this.kpiID
        };

        this.removeMetricDocument(params)
            .then( res => {
                this.$toast.open({
                    message: this.$parseResMessage(res.data.messageName),
                    type: 'success',
                    dismissible: true,
                    duration: 5000
                });
            })
            .catch(({ response }) => {
                this.$toast.open({
                    message: this.$parseResMessage(response.data.messageName),
                    type: 'error',
                    dismissible: true,
                    duration: 5000
                });
            })
            .finally(() => {
              if (this.searchQuery.length > 0) {
                this._searchDocument();
              }  
              this._getAllDocuments(); // #TODO: Get the existing links and update the view //
              this.resetAll();
            })
    },

    _removeSubmetricDocument() {
        const params = {
            document_id: this.selectedDocumentID,
            sub_metric_kpi_id: this.kpiID
        };

        this.removeSubmetricDocument(params)
            .then( res => {
                this.$toast.open({
                    message: this.$parseResMessage(res.data.messageName),
                    type: 'success',
                    dismissible: true,
                    duration: 5000
                });
            })
            .catch(({ response }) => {
                this.$toast.open({
                    message: this.$parseResMessage(response.data.messageName),
                    type: 'error',
                    dismissible: true,
                    duration: 5000
                });
            })
            .finally(() => {
              if (this.searchQuery.length > 0) {
                this._searchDocument();
              }  
              this._getAllDocuments(); // #TODO: Get the existing links and update the view //
              this.resetAll();
            })
    },

    // utility functions //

    async parseQuery() {
      try {
        var query = this.query;
        var parsedQueries = query.split('/');
      } catch (error) {
        console.error(error);
      } finally {
        this.parsedQuery = parsedQueries;
      }
    },

    getIconForType(type, name) {
      if (name === "TRASH" || name === "KPIs" || name === "MY_DOCUMENTS") {
        return this.getSpecialIconForFolder(name);
      } else {
        const icon = this.grid_data.find(item => type.includes(item.type)); // did includes since type comes "probably" with a dot //
        return icon.icon;
      }
    },

    getSpecialIconForFolder(name) {
      if (name === "TRASH") {
        return "fa-solid fa-trash";
      } else if (name === "KPIs") {
        return "fa-solid fa-chart-line";
      } else if (name === "MY_DOCUMENTS") {
        return "fa-solid fa-folder";
      }
    },

    async typeNormalizer(type) {
      if (type.includes('pdf') || type.includes('jpg') || type.includes('png') || type.includes('doc') || type.includes('xls')) // did includes since type comes "probably" with a dot //
      { 
        return 'file';
      } else if (type == 'folder') {
        return 'folder';
      } else if (type === undefined || !type || type === 'empty'){
        return 'empty';
      }
    },

    normalizeName(name) {
      const indexOfDot = name.indexOf('.');

      if (indexOfDot > 0 && indexOfDot) {
        let normalizedName = name.substring(0, indexOfDot);
        return normalizedName.replace(/_/g, ' ');
      } else {
        return name.replace(/_/g, ' ');
      }
    },

    capitalizeWord(str) {
      if (str === "documents") {
        return str.toUpperCase();
      } else {
        return str;
      }
    },

    cutOffNameGrid(name) {
      if (name.length > 7) {
        let splitName = name.split(' ');
        splitName = splitName[0].substring(0, 7) + '...'
        return [splitName, true];
      } else {
        return [name, false];
      }
    },
    cutOffNameList(name) {
      if (name.length > 20) {
        name = name.substring(0, 20) + '...'
        return [name, true];
      } else {
        return [name, false];
      }
    },

    substringToOccurrence(string, occurrence, n) {
      let index = 0;
      let substring = "";

      for (let i = 0; i < string.length; i++) {
        if (string[i] === occurrence) {
          index++;
        }
        if (index === n) {
          substring = string.substring(0, i);
          break;
        } else {
          continue;
        }
      }

      return substring;
    },

    getFileTypeID(fileName) {
      const indexOfDot = fileName.lastIndexOf('.');
      const fileType = fileName.substring(indexOfDot+1, fileName.length);
      return this.documentTypes.find(item => item.document_type_name.includes(fileType)).document_type_id;
    },

    // event handlers //

    handleLeftClick(event, item) {
      this.resetSearch();
      const normalizedItem = {
        name: item
      }

      this.goToFolder(normalizedItem);
    },

    handleDoubleClick(event, item) {
      console.log(item.extension)
      if (item.extension === "folder") {
        this.goToFolder(item);
      } else if (item.extension.includes("pdf") || item.extension.includes("jpg") || item.extension.includes("png") || item.extension.includes("doc") || item.extension.includes("xls")) {
        try {
          this.selectedDocumentID = item.info.document_id;
          this.token = localStorage.getItem('access_token');
          this._viewDocument();
        } catch (error) {
          console.error(error);
        } finally {
          this.viewDocumentModal = true;
        }
      } else {
        throw new Error("Invalid item tried to open!" + item);
      }
    },

    async handleRightClick(event, type, item) {
      try {
        this.menuType = await this.typeNormalizer(type);
        var eventHandler = event;
      } catch (error) {
        console.error(error);
      } finally {
        await this.openContextMenu(eventHandler, item);
      }
    },

    handleDragOver(event) {
      this.isDraggedOver = true
    },

    handleDragEnter(event) {
      this.isDraggedOver = true;
    },

    handleDragLeave(event) {
      this.isDraggedOver = false;
    },

    // file & folder move handlers //

    handleMoveStart(event) {
      this.movedItem = this.documents.find(item => item.name === event.target.id);
      this.isMovedOver = true;
    },

    handleMoveOver(event) {
      this.isMovedOver = true

      this.movedOver = event.target.id;
    },

    handleMoveEnter(event) {
      this.isMovedOver = true;
    },

    handleMoveLeave(event) {
      this.isMovedOver = false;
      this.movedOver = "";
    },

    handleMoveEnd(event) {
      try {
        this.isMovedOver = false;
        this.movedTo = event.target.id;
      } catch (error) {
        console.error(error);
      } finally {
        this.movedOver = "";
        if (this.movedTo === 'selected-files') {
          this.linkController(this.movedItem.info.document_id);
        } else if (this.movedTo === 'unselected-files') {
          this.removeController(this.movedItem.info.document_id);
        } else {
          throw new Error("Invalid move operation!");
        }

        /* if (this.movedTo.extension === "folder") {
          if (this.movedItem.extension === "folder") {
            this._moveFolder();
          } else if (this.movedItem.extension !== "pdf" || this.movedItem.extension !== "jpg" || this.movedItem.extension !== "png" || this.movedItem.extension !== "docx" || this.movedItem.extension !== "xlsx") {
            this._moveDocument();
          } else {
            throw new Error("Invalid move operation!");
          }
        } else {
          throw new Error("Invalid move operation!");
        } */
      }
    },

    // handler functions //

    goToFolder(item) {
      if (this.parsedQuery[this.parsedQuery.length -1] === item.name) { // if trying to go to the document where user is currently already in, do nothing //
        return; // #TODO: Fix bug where user can't advance to next level of folder of the same name //
      }

      const index = this.parsedQuery.indexOf(item.name);

      if ((index+1) === this.folderLevel && index !== -1) {
        this.query = `${this.substringToOccurrence(this.query, "/", index+1)}/${item.name}`;
        this._getAllDocuments();
      }
      if ((index+1) < this.folderLevel && index !== -1) {
        this.query = `${this.substringToOccurrence(this.query, "/", index+1)}`;
        this._getAllDocuments();
      }
      if ((index+1) > this.folderLevel || index === -1) {
        this.query = `${this.query}/${item.name}`;
        this._getAllDocuments();
      }
    },

    changeView(view) {
      this.resetSearch();
      this.view = view;
    },

    // context menu handlers //

    async openContextMenu(event, item) {
      try {
        this.contextMenuX = `${event.layerX+5}px`;
        this.contextMenuY = `${event.layerY+5}px`;
      } catch (error) {
        console.log(error);
      } finally {
        if (item == undefined) {
          this.contextMenuItem.extension = "empty";
        } else {
          this.contextMenuItem = item;
        }
        this.contextMenu = true;
      }
    },
    closeContextMenu() {
      this.menuType = "";
      this.contextMenuItem = {};
      this.contextMenu = false;
      this.contextMenuX = 0;
      this.contextMenuY = 0;
    },

    clickOpen_ContextMenu() {
      if (this.contextMenuItem.extension === "folder") {
        this.goToFolder(this.contextMenuItem);
      } else if (this.contextMenuItem.extension.includes("pdf") || this.contextMenuItem.extension.includes("jpg") || this.contextMenuItem.extension.includes("png")) {
        try {
          this.selectedDocumentID = this.contextMenuItem.info.document_id;
          this.token = localStorage.getItem('access_token');
          this._viewDocument();
        } catch (error) {
          console.error(error);
        } finally {
          this.viewDocumentModal = true;
        }
      } else if ( this.contextMenuItem.extension.includes("doc") || this.contextMenuItem.extension.includes("xls")) {
        try {
          this.selectedDocumentID = this.contextMenuItem.info.document_id;
          this.token = localStorage.getItem('access_token');
          this.viewDocumentModal = false;

        } catch (error) {
          console.error(error);
        } finally {
          this._viewDocument();
        }
      } else {
        throw new Error("Invalid context menu item tried to open!" + this.contextMenuItem);
      }

      this.closeContextMenu();
    },

    destroyToken() {
      this.token = '';
    },

    // general api controllers //

    uploadController() {
        if (this.readOnly) {
          this.$toast.open({
              message: i18n.t('documentation.readonly_suppliers'),
              type: 'error',
              dismissible: true,
              duration: 5000
          });
          
          return;
        }

        if (this.kpiType === 'metric') {
          this._uploadMetricDocument();
        } else if (this.kpiType === 'submetric') {
          this._uploadSubmetricDocument();
        } else {
          throw new Error("Invalid KPI Type!");
        }
    },

    linkController(documentID) {
        if (this.readOnly) {
          this.$toast.open({
              message: i18n.t('documentation.readonly_suppliers'),
              type: 'error',
              dismissible: true,
              duration: 5000
          });
          
          return;
        }

      this.resetSearch();
      this.selectedDocumentID = documentID;
      if (this.kpiType === 'metric') {
        this._linkMetricDocument();
      } else if (this.kpiType === 'submetric') {
        this._linkSubmetricDocument();
      } else {
        throw new Error("Invalid KPI Type!");
      }
    },

    removeController(documentID) {
        if (this.readOnly) {
          this.$toast.open({
              message: i18n.t('documentation.readonly_suppliers'),
              type: 'error',
              dismissible: true,
              duration: 5000
          });
          
          return;
        }

      this.selectedDocumentID = documentID;
      if (this.kpiType === 'metric') {
        this._removeMetricDocument();
      } else if (this.kpiType === 'submetric') {
        this._removeSubmetricDocument();
      } else {
        throw new Error("Invalid KPI Type!");
      }
    },

    renameController() {
        if (this.readOnly) {
          this.$toast.open({
              message: i18n.t('documentation.readonly_suppliers'),
              type: 'error',
              dismissible: true,
              duration: 5000
          });
          
          return;
        }

      if (this.contextMenuItem.extension === "folder") {
        this.openRenameFolderModal();
      } else {
        this.openRenameFileModal();
      }
    },

    isDocumentLinkedController(item) {
      if (this.kpiType === 'metric') {
        if (item.info.metric_kpi_ids != [] && item.info.metric_kpi_ids !== null && item.info.metric_kpi_ids != undefined) { // if item has at least a single link
          if (item.info.metric_kpi_ids.includes(this.kpiID)) { // if the item is linked to the current metric
            return true;
          } else {
            return false;
          }
        } else {
          return false;
        }
      } else if (this.kpiType === 'submetric') {
        if (item.info.sub_metric_kpi_ids != [] && item.info.sub_metric_kpi_ids !== null && item.info.sub_metric_kpi_ids) { // if item has at least a single link
          if (item.info.sub_metric_kpi_ids.includes(this.kpiID)) { // if the item is linked to the current submetric
            return true;
          } else {
            return false;
          }
        } else {
          return false;
        }
      } else {
        throw new Error("Invalid KPI Type!");
      }
    },

    // file modal handlers //

    openAddFileModal() {
      this.$refs.fileBrowse.click();
    },

    openRenameFileModal() {
      this.selectedDocumentExtension = this.contextMenuItem.extension;
      this.selectedDocumentID = this.contextMenuItem.info.document_id;
      this.renameFileModal = true;
    },

    async selectFile() {
      try {
        this.files = this.$refs.fileBrowse.files[0];
        this.documentTypeID = this.getFileTypeID(this.files.name);
      } catch (error) {
        console.error(error);
      } finally {
        this.uploadController();
      }
    },

    async handleFileDrop(event) { // might add dropping directly into folders in the future //
      try {
        this.files = event.dataTransfer.files[0];
        this.documentTypeID = this.getFileTypeID(this.files.name);
      } catch (error) {
        console.error(error);
      } finally {
        this.uploadController();
        this.isDraggedOver = false;
      }
    },

    resetFormData() {
      this.files = null;
      this.documentTypeID = '';
      this.documentPath = '';
      this.documentName = '';
      this.selectedDocumentID = '';
      this.selectedDocumentName = '';
      this.selectedDocumentPath = '';
      this.selectedDocumentExtension = '';
      this.newDocumentName = '';
      this.folderName_Create = '';
      this.folderName_Edit = '';
      this.newFolderName = '';
    },

    resetAll() {
      this.resetFormData();
      this.closeContextMenu();
      this.movedItem = {};
      this.movedOver = {};
      this.movedTo = {};
      this.allDocuments = [];

      this.$emit("reloadMetrics");
    }
  },
}
</script>

<template>
  <div id="wrapper">
    <div class="route-identifier pb-1">
      <div class="route" v-for="(item, index) in parsedQuery" :key="index">
        <span>{{ capitalizeWord(normalizeName(item)) }}</span>
        <span style="color: black;"> >&nbsp;</span> <!-- arrow -->
      </div>

      <div class="search-field">
        <b-form-input :placeholder="$t('documentation.buttons.search_placeholder')" size="sm" v-model="searchQuery" @keydown.enter="_searchDocument"></b-form-input>
        <!-- <b-button class="ml-1" variant="success" size="sm" @click="_searchDocument">{{ $t('documentation.buttons.search') }}</b-button> -->
        <i class="fa-solid fa-magnifying-glass search-icon__no-scale" @click="_searchDocument"></i>
      </div>

      <div class="view-buttons">
        <div class="p-2" @click="changeView('grid')" :class="{'selected': view === 'grid'}" v-b-tooltip.hover="{title: $t('documentation.tooltip_fields.grid'), placement: 'bottom', customClass: 'tooltip'}">
          <i class="fa-solid fa-grip"></i>
        </div>
        <div class="p-2" @click="changeView('list')" :class="{'selected': view === 'list'}" v-b-tooltip.hover="{title: $t('documentation.tooltip_fields.list'), placement: 'bottom', customClass: 'tooltip'}">
          <i class="fa-solid fa-bars"></i>
        </div>
      </div>
    </div>
    <div v-if="view === 'list'">
      <div class="folders-and-files"
        @contextmenu.prevent.self.stop="handleRightClick($event, 'empty')"
        :class="{'dragged-over': isDraggedOver}"
      >
        <div class="headers">
          <div class="header">{{ $t('documentation.list_fields.name') }}</div>
          <!-- <div class="header">Uploaded By</div> might need the query returns for getting the name of who -->
          <div class="header">{{ $t('documentation.list_fields.date') }}</div>
          <div class="header">{{ $t('documentation.list_fields.type') }}</div>
          <div class="header">{{ $t('documentation.list_fields.size') }}</div>
        </div>

        <div class="items" style="border-bottom: medium solid var(--background-fade);">
          <h5 class="pt-3">{{ $t('documentation.selected') }}</h5>
          <div v-if="selectedFiles.length === 0">{{ $t('documentation.nothing') }}</div>
          <div class="item" @dblclick="handleDoubleClick($event, item)" v-for="(item, index) in selectedFiles" :key="index"
            @contextmenu.prevent="handleRightClick($event, item.extension, item)"
          >
            <div class="name">
              <i v-if="!isDocumentLinkedController(item)" class="pt-1 fa-regular fa-square-plus link-document-icon"></i>
              <i v-else class="pt-1 fa-solid fa-regular fa-square-minus unlink-document-icon"></i> <!-- will be added with the api new request -->
              <i class="pt-1 pl-2 pr-3" :class="[getIconForType(item.extension, item.name)]"></i> 
              <span v-b-tooltip.hover="{ title: item.normalizedName, placement: 'bottom', disabled: cutOffNameList(item.normalizedName)[1] == false, customClass: 'tooltip' }">{{ cutOffNameList(item.normalizedName)[0] }}</span>
            </div>
            <!-- <div class="name">{{ item.normalizedName }}</div> -->
            <div class="date-modified">{{ (item.info.updated_on || item.info.uploaded_on) | dateFormat  }}</div>
            <div class="type">{{ item.extension }}</div>
            <div class="size">{{ item.fileSizeInBytes | byteToKB}}</div>
          </div>
        </div>
        <div class="items">
          <h5 class="pt-3">{{ $t('documentation.others') }}</h5>
          <div v-if="unselectedFiles.length === 0">{{ $t('documentation.nothing') }}</div>
          <div class="item" @dblclick="handleDoubleClick($event, item)" v-for="(item, index) in unselectedFiles" :key="index"
            @contextmenu.prevent="handleRightClick($event, item.extension, item)"
          >
            <div class="name">
              <i v-if="!isDocumentLinkedController(item)" class="pt-1 fa-regular fa-square-plus link-document-icon"></i>
              <i v-else class="pt-1 fa-solid fa-regular fa-square-minus unlink-document-icon"></i> <!-- will be added with the api new request -->
              <i class="pt-1 pl-2 pr-3" :class="[getIconForType(item.extension, item.name), {'is-selected-metric': isDocumentLinkedController(item)}]"></i> 
              <span v-b-tooltip.hover="{ title: item.normalizedName, placement: 'bottom', disabled: cutOffNameList(item.normalizedName)[1] == false, customClass: 'tooltip' }">{{ cutOffNameList(item.normalizedName)[0] }}</span>
            </div>
            <!-- <div class="name">{{ item.normalizedName }}</div> -->
            <div class="date-modified">{{ (item.info.updated_on || item.info.uploaded_on) | dateFormat  }}</div>
            <div class="type">{{ item.extension }}</div>
            <div class="size">{{ item.fileSizeInBytes | byteToKB}}</div>
          </div>
        </div>
      </div>
    </div>
    <h5 v-if="view === 'grid'" class="pt-3">{{ $t('documentation.selected') }}</h5>
    <div v-if="selectedFiles.length === 0 && view === 'grid'">{{ $t('documentation.nothing') }}</div>
    <div class="grid-view" v-if="view === 'grid'" style="border-bottom: medium solid var(--background-fade);" id="selected-files" 
      @contextmenu.prevent.self.stop="handleRightClick($event, 'empty')"
      :class="{'dragged-over': movedOver === 'selected-files'}"
    >
      <div class="item-grid" v-for="(item, index) in selectedFiles" :key="index" 
        @dblclick="handleDoubleClick($event, item)" @contextmenu.prevent="handleRightClick($event, item.extension, item)"
        v-b-tooltip.hover="{ title: item.normalizedName, placement: 'bottom', disabled: cutOffNameGrid(item.normalizedName)[1] == false, customClass: 'tooltip' }"
        :draggable="true" :id="item.name"
        :class="{'is-selected-metric': isDocumentLinkedController(item)}"
      >
        <div class="grid-icon">
          <i :class="[getIconForType(item.extension, item.name)]"></i>
        </div>
        <div class="grid-name">
          {{ cutOffNameGrid(item.normalizedName)[0] }}
        </div>
      </div>
    </div>
    <h5 v-if="view === 'grid'" class="pt-3">{{ $t('documentation.others') }}</h5>
    <div v-if="unselectedFiles.length === 0 && view === 'grid'">{{ $t('documentation.nothing') }}</div>
    <div class="grid-view" v-if="view === 'grid'" id="unselected-files" 
      @contextmenu.prevent.self.stop="handleRightClick($event, 'empty')"
      :class="{'dragged-over': movedOver === 'unselected-files'}"
    >
      <div class="item-grid" v-for="(item, index) in unselectedFiles" :key="index" 
        @dblclick="handleDoubleClick($event, item)" @contextmenu.prevent="handleRightClick($event, item.extension, item)"
        v-b-tooltip.hover="{ title: item.normalizedName, placement: 'bottom', disabled: cutOffNameGrid(item.normalizedName)[1] == false, customClass: 'tooltip' }"
        :id="item.name"
        :class="{'is-selected-metric': isDocumentLinkedController(item)}"
      >
        <div class="grid-icon">
          <i :class="[getIconForType(item.extension, item.name)]"></i>
        </div>
        <div class="grid-name">
          {{ cutOffNameGrid(item.normalizedName)[0] }}
        </div>
      </div>
    </div>

    <div class="context-menu" :style="{left: contextMenuX, top: contextMenuY}" v-clickoutside="closeContextMenu" v-if="contextMenu">
        <div class="context-menu-item" v-if="(menuType === 'folder' || menuType === 'file') && !(contextMenuItem.extension.includes('doc') || contextMenuItem.extension.includes('xls'))" @click="clickOpen_ContextMenu()">{{ $t('documentation.context_menu.open') }}</div>
        <div class="context-menu-item" v-if="(contextMenuItem.extension.includes('doc') || contextMenuItem.extension.includes('xls'))" @click="clickOpen_ContextMenu()">{{ $t('documentation.context_menu.download') }}</div>
    </div>

    <!-- // MODALS // -->

    <b-modal
      v-model="viewDocumentModal"
      size="xl" centered
      hide-footer
      @hidden="destroyToken()"
    >
      <div class="document-view-wrapper">
        <iframe class="document-viewer" :src="documentToView" frameborder="0" width="100%" height="800px"></iframe>
      </div>
    </b-modal>

  </div>  
</template>

<style lang="scss" scoped>
  #wrapper {
    position: relative;

    display: flex;
    flex-direction: column;

    height: 70vh;

    overflow-y: auto;

    padding: 1em;
    margin: .5em;

    border-radius: 1em;
    filter: drop-shadow(0 0 .5em gray);

    background-color: var(--sustainfinity-faded-blue);
  }

  .route-identifier {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    font-size: 1.5em;
    font-weight: 600;

    padding-left: 1em;

    border-bottom: medium solid var(--background-fade);

    & > .route {
      white-space: nowrap; // important for when the route has whitespaces (will have white spaces via. utility functions in the future)
      cursor: default;
    }
  }

  .search-field {
    margin-left: .33em;
    width: 100%;

    display: flex;
    flex-direction: row;
    justify-content: flex-end;

    > .search-icon__no-scale {
      cursor: pointer;
      padding-top: .2em;
      padding-left: .2em;

      &:hover {
        color: black;
      }
    }
  }

  .folders-and-files {
    display: flex;
    flex-direction: column;

    min-height: 40vh;

    & > .headers {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      padding: 1em;
      font-weight: 600;
      font-size: 1.2em;

      border-bottom: .1em solid var(--background-fade);

      & > .header {
        text-align: right;
        width: 100%; // #TODO: might have to look back at this //
      }
      & > :first-child {
        text-align: left;
        padding-left: .33em;
      }
    }

    & > .items {
      display: flex;
      flex-direction: column;

      & > .item {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        text-align: right;

        padding-left: .33em;
        padding-right: .33em;

        > .name {
          display: flex;
          flex-direction: row;
          justify-content: flex-start;
          align-items: center;

          position: relative;

          > i {
            padding-bottom: 1em;
          }
        }

        font-size: 1.2em;
        font-weight: 500;

        &:nth-child(odd) {
          background-color: var(--sustainfinity-faded-blue-darker);
        }
        &:first-child {
          margin-top: .5em;
        }

        border-radius: .8em;
        & > div {
          overflow: hidden;
          width: 100%; // #TODO: might have to look back at this //
          padding-top: .4em;
          padding-bottom: .4em;

          -webkit-touch-callout: none; // for better user experience on item double-click //
          -webkit-user-select: none; // for better user experience on item double-click //
          -khtml-user-select: none; // for better user experience on item double-click //
          -moz-user-select: none; // for better user experience on item double-click //
          -ms-user-select: none; // for better user experience on item double-click //
          user-select: none; // for better user experience on item double-click //
        }
        :first-child {
          text-align: left;

          -webkit-touch-callout: text; // for better user experience on item double-click //
          -webkit-user-select: text; // for better user experience on item double-click //
          -khtml-user-select: text; // for better user experience on item double-click //
          -moz-user-select: text; // for better user experience on item double-click //
          -ms-user-select: text; // for better user experience on item double-click //
          user-select: text; // for better user experience on item double-click //
        }

        &:hover {
          background-color: var(--background-fade);
        }
      }
    }
  }

  .view-buttons {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    width: 100%;
    padding-right: 1em;

    :first-child {
      border-radius: .5em !important;

      & > i {
        padding-top: .1em;
        padding-left: .33em;
      }
    }

    & > div {
      font-size: .9em;
      padding: .33em !important;

      margin-right: .33em !important;
      border-radius: 1em;
      cursor: pointer;
      &:hover {
        background-color: var(--background-fade);
      }
    }

    & > .selected {
      border-radius: 1em;
      background-color: #a9a9a93b;
      pointer-events: none;
    }
  }

  .grid-view {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-content: baseline;
    padding: 1em;

    height: 100vh;

    flex-wrap: wrap;

    & > .item-grid {
      border-bottom: thin solid black;

      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      padding: 1em;
      margin: .5em;
      border: thin solid var(--background-fade);
      border-radius: .5em;

      min-width: 5em;
      width: 7.5%;
      height: fit-content;
      overflow: hidden;

      cursor: default;

      & > .grid-icon {
        font-size: 3em;
      }

      & > .grid-name {
        text-align: center;

        font-size: 1.2em;
        font-weight: 600;
      }

      &:hover {
        background-color: var(--background-fade);
      }
    }
  }

  .document-view-wrapper {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    align-content: center;
    padding: 1em;

    height: fit-content;
    width: 100%;

    > .document-viewer {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      align-content: center;

      width: 100%;
      height: 100%;

      min-height: 70em;
      max-height: 1200px;
    }
  }

  .context-menu {
    position: absolute;
    width: 10%;
    height: fit-content;

    border-radius: .5em;
    outline: thin solid gray;
    filter: drop-shadow(0 0 .33em black);

    color: white;

    display: flex;
    flex-direction: column;

    background-color: darkgray;
    opacity: .9;

    > .context-menu-item {
      padding: .4em;
      border-bottom: thin solid gray;

      &:last-of-type {
        border-bottom: none;
      }

      cursor: pointer;
      &:hover {
        background-color: rgb(193, 193, 193);
      }
    }
  }

  .dragged-over {
    outline: thin dashed var(--sustainfinity-green);
    box-shadow: 0 0 1em .2em var(--sustainfinity-green) inset;
  }
  .dragged-over__invalid {
    outline: thin dashed var(--invalid-input);
    box-shadow: 0 0 1em .2em var(--invalid-input) inset;
  }

  .drag-active {
    outline: thin dashed yellow;
    box-shadow: 0 0 1em .2em yellow inset; // #TODO: fix bug on dragend event or report it //
  }

  .move-select-field {
    width: 80%;
    padding: .5em;
    margin: .5em;
    border-radius: .5em;
  
    > option {
      font-size: 1.25em;
    }
  }

  .link-document-icon, .unlink-document-icon {
    cursor: pointer;
  }

  .link-document-icon {
    color: var(--sustainfinity-green);
  }
  .unlink-document-icon {
    color: var(--invalid-input);
  }
  .link-document-icon:hover {
    filter: brightness(1.1);
  }
  .unlink-document-icon:hover {
    filter: brightness(1.1);
  }

  .linked-item-indicator {
    color: var(--sustainfinity-green);
    font-size: 1.5em;
  }

  .is-selected-metric {
    color: var(--sustainfinity-green);
  }

</style>

<style>
  .tooltip > .tooltip-inner {
    background-color: var(--sustainfinity-navy);
  }
</style>